import { useTranslation } from 'react-i18next'
import { useState, useMemo } from 'react'
import * as Yup from 'yup'
import { FormikProvider, useFormik } from 'formik'
import { useDebounceValue } from 'usehooks-ts'

import { useGrantsUtils } from '../../../utils/utils'
import { useDateChangeHandler } from '../../hooks/useDateChangeHandler'
import { useBooleanSelectOption } from '../../hooks/useBooleanSelectOption'
import { useCandidateRuleOption } from '../../hooks/useCandidateRuleOption'
import { getBaseProgramValidation } from '../../helpers/programValidation'

import { AcknowledgeModalState, AddProgramModalProps, FormValues } from './AddProgramModal.types'
import {
  GrantProgramEntryType,
  GrantProgramCandidateRule,
  AddGrantProgramPayload
} from '@percent/admin-dashboard/api/actions/grantProgram/grantProgram.types'
import {
  SUPPRESSIBLE_EMAIL_NAMES,
  SuppressibleEmailName
} from '@percent/admin-dashboard/api/actions/grantProgram/suppressibleEmailNames'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useMutation, useQueryList } from '@percent/utility'
import {
  AcknowledgeModal,
  ActionModal,
  AsyncSelect,
  DateInput,
  Flex,
  FormField,
  MarkdownTextArea,
  Modal,
  MultiSelect,
  Select,
  TextInput
} from '@percent/lemonade'

const DEFAULT_STATE_MACHINE_DEFINITION = '{ "version": 2, "states": [] }'

export function AddProgramModal({ open, setOpen, onAdd }: Readonly<AddProgramModalProps>) {
  const [modalState, setModalState] = useState<AcknowledgeModalState | null>(null)
  const [partnerSearchQuery, setPartnerSearchQuery] = useState('')
  const [selectedPartnerLabel, setSelectedPartnerLabel] = useState<string>('')
  const { grantProgramService, adminService } = useServices()
  const { t } = useTranslation()
  const { toIsoOrNull, toNullIfEmpty, toNumberOrNull } = useGrantsUtils()

  const [debouncedPartnerQuery] = useDebounceValue(partnerSearchQuery, 300)
  const [{ data: partnerListData, isLoading: isPartnersLoading }] = useQueryList(
    ['/v1/admin/partners', debouncedPartnerQuery],
    adminService.queries.getPartnerList,
    { query: debouncedPartnerQuery, pageSize: 20 },
    { enabled: debouncedPartnerQuery.length >= 1 }
  )

  const partnerOptions = useMemo(
    () =>
      (partnerListData ?? []).map(partner => ({
        value: partner.id,
        label: partner.name
      })),
    [partnerListData]
  )

  const [{ isPending }, { apiFunc: addProgram }] = useMutation(
    grantProgramService.addProgram,
    () => {
      setModalState('success')
      onAdd?.()
    },
    () => setModalState('error')
  )

  const initialValues = useMemo<FormValues>(
    () => ({
      name: '',
      partnerId: '',
      stateMachineDefinition: DEFAULT_STATE_MACHINE_DEFINITION,
      validationSubmissionConfigurationId: '',
      entryType: GrantProgramEntryType.NOMINATION,
      candidateRule: GrantProgramCandidateRule.UNIQUE_PER_ORG,
      description: '',
      opensAt: null,
      closesAt: null,
      budgetMin: undefined,
      budgetMax: undefined,
      currency: undefined,
      nominationFormId: '',
      nominationDeadline: null,
      nonprofitCcEmail: null,
      shareNominatorInfoWithOrg: false,
      emailSuppressionConfig: []
    }),
    []
  )

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string()
          .max(255, t('errorMessage.orgFieldCharacterLimit', { fieldName: 'Name' }))
          .required(),
        partnerId: Yup.string().required(),
        stateMachineDefinition: Yup.string().required(),
        validationSubmissionConfigurationId: Yup.string().nullable().required(),
        entryType: Yup.mixed<GrantProgramEntryType>()
          .oneOf([
            GrantProgramEntryType.NOMINATION,
            GrantProgramEntryType.APPLICATION,
            GrantProgramEntryType.INVITATION
          ])
          .required(),
        description: Yup.string().optional(),
        nominationFormId: Yup.string().when('entryType', {
          is: GrantProgramEntryType.INVITATION,
          then: schema => schema.optional(),
          otherwise: schema => schema.required()
        }),
        ...getBaseProgramValidation(t)
      }),
    [t]
  )

  const normalizeValues = (values: FormValues): AddGrantProgramPayload => ({
    name: values.name,
    partnerId: values.partnerId,
    stateMachineDefinition: DEFAULT_STATE_MACHINE_DEFINITION,
    validationSubmissionConfigurationId: values.validationSubmissionConfigurationId,
    entryType: values.entryType,
    candidateRule: values.candidateRule,
    description: values.description || undefined,
    opensAt: toIsoOrNull(values.opensAt),
    closesAt: toIsoOrNull(values.closesAt),
    nominationDeadline: toIsoOrNull(values.nominationDeadline),
    budgetMin: toNumberOrNull(values.budgetMin) || undefined,
    budgetMax: toNumberOrNull(values.budgetMax) || undefined,
    currency: values.currency || undefined,
    nominationFormId: values.nominationFormId || undefined,
    nonprofitCcEmail: toNullIfEmpty(values.nonprofitCcEmail) ?? undefined,
    shareNominatorInfoWithOrg: values.shareNominatorInfoWithOrg ?? false,
    emailSuppressionConfig: values.emailSuppressionConfig?.length ? values.emailSuppressionConfig : undefined
  })

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    validateOnMount: true,
    validateOnChange: true,
    initialValues,
    validationSchema,
    onSubmit: async values => {
      const payload = normalizeValues(values)
      addProgram(payload)
    }
  })

  const { values, errors, touched, dirty, handleChange, handleBlur, handleSubmit, setFieldValue, resetForm, isValid } =
    formik

  const isEntryTypeInvitation = values.entryType === GrantProgramEntryType.INVITATION

  const getFieldStatus = (fieldName: keyof FormValues) =>
    touched[fieldName] && errors[fieldName] ? 'danger' : 'default'

  const handleDateChange = useDateChangeHandler<FormValues>(setFieldValue)

  const shareNominatorInfoWithOrgOption = useBooleanSelectOption(values.shareNominatorInfoWithOrg)

  const entryTypeLabels: Record<GrantProgramEntryType, string> = useMemo(
    () => ({
      [GrantProgramEntryType.NOMINATION]: t('grants.program.modal.entryType.nomination'),
      [GrantProgramEntryType.APPLICATION]: t('grants.program.modal.entryType.application'),
      [GrantProgramEntryType.INVITATION]: t('grants.program.modal.entryType.invitation')
    }),
    [t]
  )

  const entryTypeOption = useMemo(
    () =>
      values.entryType
        ? {
            label: entryTypeLabels[values.entryType],
            value: values.entryType
          }
        : undefined,
    [values.entryType, entryTypeLabels]
  )

  const { selectedOption: candidateRuleOption, options: candidateRuleOptions } = useCandidateRuleOption(
    values.candidateRule
  )

  const emailSuppressionOptions = useMemo(
    () => SUPPRESSIBLE_EMAIL_NAMES.map(name => ({ label: name, value: name })),
    []
  )

  // Filter the memoized options array (not a new map) so the resulting object
  // references match exactly — `useMultiSelect` checks `selected.includes(option)`
  // by reference, so a freshly-mapped object would never tick the checkbox.
  const emailSuppressionDefaultValue = useMemo(
    () =>
      emailSuppressionOptions.filter(opt =>
        (values.emailSuppressionConfig ?? []).includes(opt.value as SuppressibleEmailName)
      ),
    [emailSuppressionOptions, values.emailSuppressionConfig]
  )

  const handleClose = () => {
    setOpen(false)
    setModalState(null)
    setPartnerSearchQuery('')
    setSelectedPartnerLabel('')
    resetForm()
  }

  const clearPartnerSelection = () => {
    setFieldValue('partnerId', '')
    setSelectedPartnerLabel('')
  }

  const successModal = modalState === 'success' && (
    <AcknowledgeModal
      result="positive"
      title={t('grants.programs.addProgram.success.title')}
      buttonText={t('button.done')}
      handleClose={handleClose}
      viewTestId="add-program-success-modal"
      buttonTestId="done-add-program-success-modal"
    />
  )

  const errorModal = modalState === 'error' && (
    <AcknowledgeModal
      result="negative"
      title={t('grants.programs.addProgram.error.title')}
      buttonText={t('button.close')}
      handleClose={handleClose}
      viewTestId="add-program-error-modal"
      buttonTestId="close-add-program-error-modal"
    />
  )

  return (
    <Modal open={open} onClose={() => setOpen(false)} showModalOverflow={false}>
      {successModal || errorModal || (
        <ActionModal
          title={t('grants.programList.addProgram.title')}
          loading={isPending}
          primaryButtonText={t('button.add')}
          primaryBtnTestId="add-program-modal-button"
          secondaryButtonText={t('button.cancel')}
          secondaryBtnTestId="cancel-add-program-modal-button"
          handleSubmit={handleSubmit}
          handleClose={handleClose}
          disabled={!(isValid && dirty)}
          maxHeight="60vh"
        >
          <form onSubmit={handleSubmit}>
            <FormikProvider value={formik}>
              <Flex direction="column" gap={16}>
                <FormField
                  label={t('grants.program.modal.name')}
                  status={getFieldStatus('name')}
                  statusMessage={errors.name}
                  data-testid="name-field"
                  necessity="required"
                >
                  <TextInput
                    name="name"
                    value={values.name ?? ''}
                    placeholder={t('grants.program.modal.name.placeholder')}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                </FormField>
                <FormField
                  label={t('grants.program.modal.partner')}
                  status={getFieldStatus('partnerId')}
                  statusMessage={errors.partnerId}
                  data-testid="partnerId-field"
                  necessity="required"
                >
                  <AsyncSelect
                    placeholder={t('grants.program.modal.partner.placeholder')}
                    options={partnerOptions}
                    query={partnerSearchQuery}
                    setQuery={q => {
                      setPartnerSearchQuery(q)

                      if (!q && selectedPartnerLabel) {
                        clearPartnerSelection()
                      }
                      handleBlur({ target: { name: 'partnerId' } })
                    }}
                    loading={isPartnersLoading}
                    noResultsFoundText={t('typography.noResultsFound')}
                    defaultValue={
                      values.partnerId && selectedPartnerLabel
                        ? { value: values.partnerId, label: selectedPartnerLabel }
                        : undefined
                    }
                    onChange={e => {
                      if (e) {
                        setFieldValue('partnerId', e.value)
                        setSelectedPartnerLabel(e.label)
                      } else {
                        clearPartnerSelection()
                      }
                    }}
                  />
                </FormField>
                <FormField
                  label={t('grants.program.modal.validationSubmissionConfigurationId')}
                  status={getFieldStatus('validationSubmissionConfigurationId')}
                  statusMessage={errors.validationSubmissionConfigurationId}
                  data-testid="validationSubmissionConfigurationId-field"
                  necessity="required"
                >
                  <TextInput
                    name="validationSubmissionConfigurationId"
                    value={values.validationSubmissionConfigurationId ?? ''}
                    placeholder="Enter validation submission configuration ID"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    data-testid="validationSubmissionConfigurationId-input"
                  />
                </FormField>
                <FormField
                  label={t('grants.program.modal.entryType')}
                  status={getFieldStatus('entryType')}
                  statusMessage={errors.entryType}
                  data-testid="entryType-field"
                  necessity="required"
                >
                  <Select
                    name="entryType"
                    placeholder={t('grants.program.modal.entryType.placeholder')}
                    options={Object.values(GrantProgramEntryType).map(type => ({
                      label: entryTypeLabels[type],
                      value: type
                    }))}
                    defaultValue={entryTypeOption}
                    onChange={event => setFieldValue('entryType', event.value)}
                    data-testid="entryType-select"
                  />
                </FormField>
                <FormField
                  label={t('grants.program.modal.candidateRule')}
                  status={getFieldStatus('candidateRule')}
                  statusMessage={errors.candidateRule}
                  data-testid="candidateRule-field"
                >
                  <Select
                    name="candidateRule"
                    placeholder={t('grants.program.modal.candidateRule.placeholder')}
                    options={candidateRuleOptions}
                    defaultValue={candidateRuleOption}
                    onChange={event => setFieldValue('candidateRule', event.value)}
                    data-testid="candidateRule-select"
                  />
                </FormField>
                <FormField
                  label={t('grants.program.modal.description')}
                  status={getFieldStatus('description')}
                  statusMessage={errors.description}
                  data-testid="description-field"
                >
                  <MarkdownTextArea
                    name="description"
                    content={values.description}
                    placeholder={t('grants.program.modal.description.placeholder')}
                    onChange={value => setFieldValue('description', value)}
                    testId="description-field"
                  />
                </FormField>
                <FormField
                  label={t('grants.program.modal.opensAt')}
                  status={getFieldStatus('opensAt')}
                  statusMessage={errors.opensAt}
                  data-testid="opensAt-field"
                >
                  <DateInput
                    name="opensAt"
                    value={values.opensAt}
                    placeholder={t('grants.program.modal.opensAt.placeholder')}
                    onChange={handleDateChange('opensAt')}
                    onBlur={handleBlur}
                    clearable
                    withinPopup
                    data-testid="opensAt-input"
                  />
                </FormField>
                <FormField
                  label={t('grants.program.modal.closesAt')}
                  status={getFieldStatus('closesAt')}
                  statusMessage={errors.closesAt}
                  data-testid="closesAt-field"
                >
                  <DateInput
                    name="closesAt"
                    value={values.closesAt}
                    placeholder={t('grants.program.modal.closesAt.placeholder')}
                    onChange={handleDateChange('closesAt')}
                    onBlur={handleBlur}
                    clearable
                    withinPopup
                    data-testid="closesAt-input"
                  />
                </FormField>
                <FormField
                  label={t('grants.program.modal.budgetMin')}
                  status={getFieldStatus('budgetMin')}
                  statusMessage={errors.budgetMin}
                  data-testid="budgetMin-field"
                >
                  <TextInput
                    name="budgetMin"
                    value={values.budgetMin?.toString() ?? ''}
                    placeholder={t('grants.program.modal.budgetMin.placeholder')}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    data-testid="budgetMin-input"
                  />
                </FormField>
                <FormField
                  label={t('grants.program.modal.budgetMax')}
                  status={getFieldStatus('budgetMax')}
                  statusMessage={errors.budgetMax}
                  data-testid="budgetMax-field"
                >
                  <TextInput
                    name="budgetMax"
                    value={values.budgetMax?.toString() ?? ''}
                    placeholder={t('grants.program.modal.budgetMax.placeholder')}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    data-testid="budgetMax-input"
                  />
                </FormField>
                <FormField
                  label={t('grants.program.modal.currency')}
                  status={getFieldStatus('currency')}
                  statusMessage={errors.currency}
                  description={t('grants.program.modal.currency.description')}
                  descriptionVariant="light"
                  data-testid="currency-field"
                >
                  <TextInput
                    name="currency"
                    value={values.currency ?? ''}
                    placeholder={t('grants.program.modal.currency.placeholder')}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    data-testid="currency-input"
                  />
                </FormField>
                <FormField
                  label={t('grants.program.modal.entryFormId')}
                  status={getFieldStatus('nominationFormId')}
                  statusMessage={errors.nominationFormId}
                  data-testid="entryFormId-field"
                  necessity={isEntryTypeInvitation ? 'optional' : 'required'}
                >
                  <TextInput
                    name="nominationFormId"
                    value={values.nominationFormId}
                    placeholder={t('grants.program.modal.entryFormId.placeholder.createProgram')}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    data-testid="entryFormId-input"
                  />
                </FormField>
                <FormField
                  label={t('grants.program.modal.entryFormDeadline')}
                  status={getFieldStatus('nominationDeadline')}
                  statusMessage={errors.nominationDeadline}
                  data-testid="entryFormDeadline-field"
                >
                  <DateInput
                    name="nominationDeadline"
                    value={values.nominationDeadline}
                    placeholder={t('grants.program.modal.entryFormDeadline.placeholder')}
                    onChange={handleDateChange('nominationDeadline')}
                    onBlur={handleBlur}
                    clearable
                    withinPopup
                    data-testid="nominationDeadline-input"
                  />
                </FormField>
                {values.entryType === GrantProgramEntryType.NOMINATION && (
                  <FormField
                    label={t('grants.program.modal.shareNominatorInfoWithOrg')}
                    status={getFieldStatus('shareNominatorInfoWithOrg')}
                    statusMessage={errors.shareNominatorInfoWithOrg}
                    data-testid="shareNominatorInfoWithOrg-field"
                  >
                    <Select
                      name="shareNominatorInfoWithOrg"
                      searchable
                      placeholder={t('grants.program.modal.shareNominatorInfoWithOrg.placeholder')}
                      options={[
                        { label: t('grants.program.modal.option.yes'), value: String(true) },
                        { label: t('grants.program.modal.option.no'), value: String(false) }
                      ]}
                      defaultValue={shareNominatorInfoWithOrgOption}
                      onChange={event => setFieldValue('shareNominatorInfoWithOrg', event.value === 'true')}
                      data-testid="shareNominatorInfoWithOrg-select"
                    />
                  </FormField>
                )}
                <FormField
                  label={t('grants.program.modal.nonprofitCcEmail')}
                  status={getFieldStatus('nonprofitCcEmail')}
                  statusMessage={errors.nonprofitCcEmail}
                  data-testid="nonprofitCcEmail-field"
                >
                  <TextInput
                    name="nonprofitCcEmail"
                    value={values.nonprofitCcEmail ?? ''}
                    placeholder={t('grants.program.modal.nonprofitCcEmail.placeholder')}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    data-testid="nonprofitCcEmail-input"
                  />
                </FormField>
                <FormField
                  label={t('grants.program.modal.emailSuppressionConfig')}
                  data-testid="emailSuppressionConfig-field"
                >
                  <MultiSelect
                    name="emailSuppressionConfig"
                    placeholder={t('grants.program.modal.emailSuppressionConfig.placeholder')}
                    options={emailSuppressionOptions}
                    defaultValue={emailSuppressionDefaultValue}
                    onChange={selected =>
                      setFieldValue(
                        'emailSuppressionConfig',
                        selected.map(o => o.value as SuppressibleEmailName)
                      )
                    }
                    allowClear
                    data-testid="emailSuppressionConfig-select"
                  />
                </FormField>
              </Flex>
            </FormikProvider>
          </form>
        </ActionModal>
      )}
    </Modal>
  )
}
